import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";

import AuthenticationService from "service/auth/AuthenticationService";
import ProtectedRoute from "components/route/ProtectedRoute";
import GuestRoute from "components/route/GuestRoute";
import ToastHost from "components/common/ToastHost";
import ActiveCallProvider from "modules/patient/components/ActiveCallProvider";
import SparkTGCallFloater from "modules/patient/components/SparkTGCallFloater";
import { CallEventsProvider } from "service/realtime/CallEventsContext";
import Login from "modules/user/components/Login";
import AppointmentsDashboard from "modules/appointments/components/AppointmentsDashboard";
import CreateAppointment from "modules/appointments/components/CreateAppointment";
import PatientDetails from "modules/patient/components/PatientDetails";
import AppointmentDetails from "modules/appointments/components/AppointmentDetails";
import HealthPlan from "modules/healthplan/components/HealthPlan";
import PrescriptionRoute from "mobile/screens/MobilePrescriptionRoute";

class App extends React.Component {
  constructor() {
    super();
    this.authenticationService = new AuthenticationService();
  }

  _authenticator = () => {
    return this.authenticationService.isLoggedIn();
  };

  render() {
    return (
      <div className="App">
        <ToastHost />
        {/* ActiveCallProvider owns the in-progress LiveKit call. Mounted
            above the Router so the AI-transcript modal survives route
            changes: minimising the transcript view to browse the app
            during a live call must not tear the LK room down. */}
        <ActiveCallProvider>
          {/* ZY-10312 — SparkTG ongoing-call provider. Lives above
              <Router> so a single EventSource survives every route
              change (navigating the Orbit app during a live phone
              call must not reconnect the stream). Inside
              ActiveCallProvider so the floater can consult
              useActiveCallMaybe and defer to the LiveKit CallFloater
              when a CM call is up. */}
          <CallEventsProvider>
        <Router>
          {/* Floater sits as a sibling of <Switch> inside <Router> so
              useHistory resolves (unlike above Router). Not matched by
              any Route, so it never unmounts on navigation — the
              persistent-across-pages behaviour comes from this
              placement, not from the provider position. */}
          <SparkTGCallFloater />
          <Switch>
            {/* GUEST-ONLY. A signed-in user who opens a login URL (typed, a
                bookmark, Back) is sent into the app, not shown the form again —
                signing in there as someone else replaced the session in every
                open tab. Opening /login never signs anyone out. */}
            <GuestRoute
              path="/login"
              component={Login}
              authenticator={this._authenticator}
              redirectTo={"/navigo-module"}
            />
            <GuestRoute
              path="/doctor/login"
              component={Login}
              authenticator={this._authenticator}
              redirectTo={"/navigo-module"}
            />
            {/* Order AND `exact` are both load-bearing. ProtectedRoute renders
                <Route exact {...this.props}/>, but that `exact` is dead inside a
                Switch: Switch matches using the child element's own props and
                injects computedMatch, which Route prefers over its own exact.
                So without `exact` here, /navigo-module matches non-exactly and
                swallows /navigo-module/patient/123. */}
            <ProtectedRoute
              exact
              path="/navigo-module/create"
              component={CreateAppointment}
              authenticator={this._authenticator}
              redirectTo={"/login"}
            />
            <ProtectedRoute
              exact
              path="/navigo-module/patient/:appointmentId"
              // Key on the appointment id so opening another appointment (e.g.
              // from the header's touchpoints dropdown) REMOUNTS the screen —
              // a full fresh load like clicking a row on the dashboard, rather
              // than re-running effects over the previous appointment's state.
              // `bookingForRev` does the same for "Update booking for": the switch
              // bumps it, so the screen reloads everything for the new member.
              render={(props) => (
                <PatientDetails
                  key={`${props.match.params.appointmentId}:${
                    (props.location.state && props.location.state.bookingForRev) || ""
                  }`}
                  {...props}
                />
              )}
              authenticator={this._authenticator}
              redirectTo={"/login"}
            />
            <ProtectedRoute
              exact
              path="/navigo-module/health-plan/:appointmentId"
              // The Health-plan editor (Figma 3141:6145) — mock-up phase. Keyed
              // so a different appointment remounts it fresh.
              render={(props) => (
                <HealthPlan key={props.match.params.appointmentId} {...props} />
              )}
              authenticator={this._authenticator}
              redirectTo={"/login"}
            />
            <ProtectedRoute
              exact
              path="/navigo-module/appointment/:appointmentId"
              // The "Appointment details" screen (Figma 2618:4041), reached by
              // clicking the Appt ID. Keyed so opening a different appointment
              // remounts it fresh.
              render={(props) => (
                <AppointmentDetails key={props.match.params.appointmentId} {...props} />
              )}
              authenticator={this._authenticator}
              redirectTo={"/login"}
            />
            {/* Mirrors Bridge's /prescription/{id}, but keyed on the appointment
                id — same as the patient screen, since that is what retrieves both
                the appointment strip and the patient it points at. */}
            {/* Through a wrapper so the mobile layer can remount the screen after
                a record is saved in a bottom sheet — see MobilePrescriptionRoute
                for why a remount and not a re-seed. On desktop the wrapper is a
                pass-through: same component, no key, no listeners. */}
            <ProtectedRoute
              exact
              path="/navigo-module/prescription/:appointmentId"
              component={PrescriptionRoute}
              authenticator={this._authenticator}
              redirectTo={"/login"}
            />
            <ProtectedRoute
              exact
              path="/navigo-module"
              component={AppointmentsDashboard}
              authenticator={this._authenticator}
              redirectTo={"/login"}
            />
            {/* The root goes where the session says: the app when signed in,
                the login screen when not. */}
            <Route
              exact
              path="/"
              render={() => (
                <Redirect to={this._authenticator() ? "/navigo-module" : "/login"} />
              )}
            />
          </Switch>
        </Router>
          </CallEventsProvider>
        </ActiveCallProvider>
      </div>
    );
  }
}

export default App;
